import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// No static `monaco-editor` import — the barrel would re-pull the ~6.5 MB TS
// language service trimmed from the bundle (same rule as CodeEditor.js).
import { invoke } from '@tauri-apps/api/core';
import { useThemeStore } from '../store/themeStore';
import './Ftp.css';
import { checkThenThrow } from './fileEdit/fileEditLanguage';
import { renderFileEditModal } from './fileEdit/renderFileEditModal';
import { useFileEditActions } from './fileEdit/useFileEditActions';
export { EXT_LANGUAGE, NAME_LANGUAGE, languageForName } from './fileEdit/fileEditLanguage';

export default function FileEditModal({
  isOpen, connId, path, name, onClose, onSaved,
  local = false,
  // Optional `{ label, name, content }`: open the editor ALREADY comparing
  // against this source. Used by the local pane's "Compare with remote file…",
  // which fetches the server file before the editor opens.
  compareWith = null,
  // Optional `{ read, write }` with the same shapes as the local pair: a
  // caller's own file source (Run section: launch.json / .env in a repo,
  // through `run_file_read` / `run_file_write`).
  backend: customBackend = null,
  // Optional `(text) => string[]`: errors that block Save, shown inline
  // (launch.json schema). Nothing is written while it returns any.
  validate = null,
}) {
  /**
   * Where the text comes from and goes back to. `local` edits a file on the
   * user's own disk (the FTP view's LOCAL pane) through `ftp_local_read_text` /
   * `ftp_local_write_text`, which return the same shapes and the same
   * `remote_changed` conflict code as the remote pair — so the whole editor,
   * conflict bar and diff view included, works unchanged on both sides. A
   * program changing a local file while it is open is the local version of the
   * server changing under you.
   */
  const backend = useMemo(
    () =>
      customBackend ||
      (local
        ? {
            read: () => invoke('ftp_local_read_text', { path }),
            write: (content, expectedMtime) =>
              invoke('ftp_local_write_text', { path, content, expectedMtime }),
          }
        : {
            // A failure can be the server gone: the FTP pane learns it now.
            read: () => invoke('ftp_read_text', { connId, path }).catch(checkThenThrow(connId)),
            write: (content, expectedMtime) =>
              invoke('ftp_write_text', { connId, path, content, expectedMtime }).catch(
                checkThenThrow(connId)
              ),
          }),
    [customBackend, local, connId, path]
  );
  const { t } = useTranslation();
  const theme = useThemeStore((s) => s.theme);
  const isDark =
    theme === 'dark' ||
    (theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  const [loading, setLoading] = useState(false);
  // Increments each time a file read RESOLVES. Auto-compare waits for this, not
  // for `loading === false`: `loading` STARTS false, so on the first commit the
  // auto-compare effect used to see "not loading" before the load effect's
  // setLoading(true) had applied. It entered compare over an empty buffer, and
  // under StrictMode's effect re-run the load effect then reset the mode to
  // 'edit' while a ref blocked a second attempt — the editor opened in plain edit
  // mode (owner report, 13.09). A counter only a resolved read can advance has
  // no such first-commit ambiguity.
  const [loadGen, setLoadGen] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [content, setContent] = useState('');
  const [savedContent, setSavedContent] = useState('');
  const [mtime, setMtime] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);
  // 'edit' | 'diff'; diff carries the freshly fetched server version.
  const [mode, setMode] = useState('edit');
  const [conflict, setConflict] = useState(false); // remote_changed bar visible
  const [latest, setLatest] = useState(null); // { content, mtime } for diff
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // "View diff" would replace an EDITED left-hand scratch copy (← merges) with
  // the server's version; that scratch exists nowhere else, so ask first.
  const [confirmDropLeft, setConfirmDropLeft] = useState(false);
  // Compare mode. `compare` is the LEFT-hand side when set:
  //   { label: 'clipboard' | 'file' | 'server', name, content }
  // `mode === 'diff'` covers both this and the save-conflict flow — they render
  // the same editor, and only the left-hand source and headers differ.
  const [compare, setCompare] = useState(null);
  const [compareMenu, setCompareMenu] = useState(false);
  const [compareError, setCompareError] = useState(null);
  const [hunks, setHunks] = useState([]);
  // The left-hand text, held separately from `compare` because ← merges edit
  // it. `compare` stays the immutable record of WHERE it came from; this is
  // what is currently shown and what the Copy button exports.
  const [compareContent, setCompareContent] = useState('');
  const [compareDirty, setCompareDirty] = useState(false);
  const [copied, setCopied] = useState(false);
  const diffEditorRef = useRef(null);

  const dirty = content !== savedContent;

  // Live refs so Monaco's addCommand callback (bound once on mount) always
  // saves the current buffer, not the mount-time closure.
  const saveRef = useRef(() => {});
  const contentRef = useRef(content);
  contentRef.current = content;
  const diffModifiedRef = useRef(null); // modified-side editor in diff mode

  const {
    detachDiffModels, doWrite, save, startCompare, endCompare, applyHunk, applyHunkReverseAction,
    copyCompareResult, openDiff, requestOpenDiff, saveDiffResult, requestClose,
  } = useFileEditActions({
    t, compare, isOpen, connId, path, name, onClose, onSaved, local, compareWith, backend, validate, loading,
    setLoading, mode, loadGen, setLoadGen, loadError, setLoadError, setContent, setSavedContent,
    mtime, setMtime, saving, setSaving, setSaveError, setSavedFlash, setMode, setConflict, latest,
    setLatest, setConfirmDiscard, confirmDropLeft, setConfirmDropLeft, setCompare, setCompareMenu,
    setCompareError, setHunks, compareContent, setCompareContent, compareDirty, setCompareDirty,
    setCopied, diffEditorRef, dirty, saveRef, contentRef, diffModifiedRef,
  });

  if (!isOpen) return null;

  return renderFileEditModal({
    t, compare, applyHunk, path, name, onClose, local, content, conflict, isDark, loading, mode,
    loadError, setContent, save, saving, saveError, savedFlash, setMode, setConflict, latest,
    confirmDiscard, setConfirmDiscard, confirmDropLeft, setConfirmDropLeft, compareMenu,
    setCompareMenu, compareError, hunks, setHunks, compareContent, compareDirty, copied,
    diffEditorRef, dirty, saveRef, contentRef, diffModifiedRef, detachDiffModels, doWrite,
    startCompare, endCompare, applyHunkReverseAction, copyCompareResult, openDiff, requestOpenDiff,
    saveDiffResult, requestClose,
  });
}
